import { SxProps, Theme } from "@mui/material";

import { sendingLoaderTokens as t } from "./CustomSendingLoader.tokens";

/**
 * Styles for the sending overlay. Every keyframe is declared on the root so
 * Emotion hoists it once per mount and the descendants can reference it by
 * name; the names are prefixed to keep them out of the global animation
 * namespace shared with the rest of the app.
 */
export const sendingLoaderStyles: Record<string, SxProps<Theme>> = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: t.overlay.zIndex,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: t.overlay.padding,
    backgroundColor: t.overlay.bg,
    backdropFilter: t.overlay.blur,
    WebkitBackdropFilter: t.overlay.blur,
    "@keyframes sendingLoaderSlideBar": {
      "0%": { left: "-45%" },
      "100%": { left: "105%" },
    },
    "@keyframes sendingLoaderDocSlide": {
      "0%": { transform: "translateY(-34px)", opacity: 0 },
      "12%": { opacity: 1 },
      "42%, 100%": { transform: "translateY(14px)", opacity: 1 },
    },
    "@keyframes sendingLoaderFlapClose": {
      "0%, 46%": { transform: "scaleY(1)" },
      "62%, 100%": { transform: "scaleY(-1)" },
    },
    "@keyframes sendingLoaderSealPress": {
      "0%, 64%": { opacity: 0, transform: "scale(1.7)" },
      "74%": { opacity: 1, transform: "scale(.92)" },
      "80%": { transform: "scale(1.08)" },
      "86%, 92%": { opacity: 1, transform: "scale(1)" },
      "100%": { opacity: 0, transform: "scale(1)" },
    },
    "@keyframes sendingLoaderDotFade": {
      "0%, 80%, 100%": { opacity: 0.25 },
      "40%": { opacity: 1 },
    },
    "@keyframes sendingLoaderDrawSig": {
      "0%": { strokeDashoffset: 420 },
      "55%, 75%": { strokeDashoffset: 0 },
      "100%": { strokeDashoffset: -420 },
    },
  },

  card: {
    width: t.card.width,
    maxWidth: "100%",
    backgroundColor: t.card.bg,
    borderRadius: t.card.radius,
    boxShadow: t.card.shadow,
    overflow: "hidden",
    position: "relative",
  },

  progressTrack: {
    height: t.progress.height,
    backgroundColor: t.progress.trackBg,
    position: "relative",
    overflow: "hidden",
  },

  progressBar: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: t.progress.barWidth,
    borderRadius: t.progress.barRadius,
    background: t.progress.barGradient,
    animation: `sendingLoaderSlideBar ${t.progress.duration} ${t.progress.easing} infinite`,
  },

  body: {
    padding: t.body.padding,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },

  envelopeStage: {
    width: t.envelope.stageWidth,
    height: t.envelope.stageHeight,
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },

  envelopeDoc: {
    position: "absolute",
    left: "50%",
    bottom: t.envelope.doc.bottom,
    width: t.envelope.doc.width,
    height: t.envelope.doc.height,
    marginLeft: `${-t.envelope.doc.width / 2}px`,
    backgroundColor: t.envelope.doc.bg,
    border: t.envelope.doc.border,
    borderRadius: t.envelope.doc.radius,
    padding: t.envelope.doc.padding,
    boxSizing: "border-box",
    zIndex: 1,
    animation: `sendingLoaderDocSlide ${t.envelope.duration} ease-in-out infinite`,
  },

  envelopeDocSignature: {
    height: t.envelope.doc.signatureHeight,
    width: t.envelope.doc.signatureWidth,
    marginTop: t.envelope.doc.signatureGap,
    borderBottom: t.envelope.doc.signatureBorder,
  },

  envelopeFront: {
    position: "relative",
    width: t.envelope.front.width,
    height: t.envelope.front.height,
    zIndex: 2,
  },

  envelopeBack: {
    position: "absolute",
    inset: 0,
    background: t.envelope.front.bg,
    border: t.envelope.front.border,
    borderRadius: t.envelope.front.radius,
  },

  envelopePocket: {
    position: "absolute",
    inset: 0,
    clipPath: t.envelope.front.pocketClip,
    background: t.envelope.front.pocketBg,
    borderRadius: t.envelope.front.radius,
  },

  envelopeFlap: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "-1px",
    height: t.envelope.flap.height,
    transformOrigin: "top center",
    zIndex: 3,
    animation: `sendingLoaderFlapClose ${t.envelope.duration} ease-in-out infinite`,
  },

  envelopeFlapFace: {
    width: "100%",
    height: "100%",
    clipPath: t.envelope.flap.clip,
    background: t.envelope.flap.bg,
    borderRadius: t.envelope.flap.radius,
    transform: "translateY(-100%)",
  },

  envelopeSeal: {
    position: "absolute",
    left: "50%",
    top: t.envelope.seal.top,
    width: t.envelope.seal.size,
    height: t.envelope.seal.size,
    marginLeft: `${-t.envelope.seal.size / 2}px`,
    borderRadius: "50%",
    background: t.envelope.seal.bg,
    boxShadow: t.envelope.seal.shadow,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 4,
    animation: `sendingLoaderSealPress ${t.envelope.duration} ease-in-out infinite`,
  },

  /**
   * The signature stage hangs the draw keyframe on its child `<path>` by class
   * rather than an inline `style`, so the animation stays in this file and the
   * SVG markup stays plain.
   */
  signatureStage: {
    width: t.signature.stageWidth,
    height: t.signature.stageHeight,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [`& .${t.signature.pathClass}`]: {
      strokeDasharray: t.signature.dashArray,
      animation: `sendingLoaderDrawSig ${t.signature.duration} ease-in-out infinite`,
    },
  },

  title: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    gap: t.title.gap,
    marginTop: t.title.marginTop,
    fontSize: t.title.fontSize,
    fontWeight: t.title.fontWeight,
    color: t.title.color,
    letterSpacing: t.title.letterSpacing,
  },

  footer: {
    padding: t.footer.padding,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: t.footer.gap,
    backgroundColor: t.footer.bg,
    borderTop: t.footer.borderTop,
  },

  footerText: {
    fontSize: t.footer.fontSize,
    fontWeight: t.footer.fontWeight,
    lineHeight: t.footer.lineHeight,
    color: t.footer.color,
  },
};

/** Copy under the title — the two designs wrap it at different widths. */
export const sendingLoaderDescription = (
  variant: keyof typeof t.description.maxWidth,
): SxProps<Theme> => ({
  marginTop: t.description.marginTop,
  fontSize: t.description.fontSize,
  lineHeight: t.description.lineHeight,
  maxWidth: t.description.maxWidth[variant],
  color: t.description.color,
});

/** The pulsing dot after the title — index drives the stagger. */
export const sendingLoaderDot = (index: number): SxProps<Theme> => ({
  fontSize: t.title.fontSize,
  fontWeight: t.title.fontWeight,
  color: t.title.dotColor,
  animation: `sendingLoaderDotFade ${t.title.dotDuration} ${t.title.dotDelays[index]} infinite`,
});

/** One placeholder text line on the animated document. */
export const sendingLoaderDocLine = (
  width: string,
  strong: boolean,
  first: boolean,
): SxProps<Theme> => ({
  height: t.envelope.doc.lineHeight,
  width,
  borderRadius: t.envelope.doc.lineRadius,
  backgroundColor: strong
    ? t.envelope.doc.lineStrongColor
    : t.envelope.doc.lineColor,
  marginTop: first ? 0 : t.envelope.doc.lineGap,
});
